<script lang="ts">
  import { router } from "@inertiajs/svelte"

  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import ConfirmationDialog from "@/components/ui/confirmation-dialog.svelte"
  import AppLayout from "@/layouts/app-layout.svelte"
  import {
    approveTaskInstancePath,
    assignTaskTemplatePath,
    completeTaskInstancePath,
    dashboardPath,
  } from "@/routes"
  import type {
    BreadcrumbItem,
    TaskInstance,
    TaskTemplate,
    User,
  } from "@/types"
  import { formatTaskLimit } from "@/utils"
  import { subscribeToDashboardChannel } from "@/utils/actioncable"

  interface Props {
    awaitingApproval?: TaskInstance[]
    currentlyAssigned?: TaskInstance[]
    adHocAvailable?: TaskTemplate[]
    unpaidCompleted?: TaskInstance[]
    financialSummary?: {
      totalUnpaid: number
      byMember: Array<{
        user: User
        totalUnpaid: number
        tasks: Array<{
          task_template: {
            id: number
            name: string
            wage: number
            payment_basis?: string
          }
          count: number
        }>
      }>
    }
    members?: User[]
    canTurnOverWeek?: boolean
  }

  let expandedMembers = $state<number[]>([])

  let {
    awaitingApproval = [],
    currentlyAssigned = [],
    adHocAvailable = [],
    unpaidCompleted = [],
    financialSummary = { totalUnpaid: 0, byMember: [] },
    members = [],
    canTurnOverWeek = false,
  }: Props = $props()

  let assignTaskTemplateId = $state<number | null>(null)
  let assignDialogRef: HTMLDialogElement | null = $state(null)
  let rejectTaskId = $state<number | null>(null)
  let feedbackText = $state<string>("")
  let turnOverWeekDialogOpen = $state(false)
  let cancelTaskId = $state<number | null>(null)
  let cancelDialogOpen = $state(false)

  $effect(() => {
    const subscription = subscribeToDashboardChannel((data) => {
      console.log("Dashboard update received:", data)
    })

    return () => {
      subscription.unsubscribe()
    }
  })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Dashboard",
      href: dashboardPath(),
    },
  ]

  function formatCompletedBy(task: TaskInstance): string {
    if (!task.assigned_to || !task.completed_at) return ""
    const date = new Date(task.completed_at)
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const timeStr = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      })
      .toLowerCase()
    return `by ${task.assigned_to.name} on ${dateStr} at ${timeStr}`
  }

  function handleApprove(taskId: number) {
    router.post(
      approveTaskInstancePath(taskId),
      {},
      {
        preserveScroll: true,
      },
    )
  }

  function handleComplete(taskId: number) {
    router.post(
      completeTaskInstancePath(taskId),
      {},
      {
        preserveScroll: true,
      },
    )
  }

  function handleRejectClick(taskId: number) {
    rejectTaskId = taskId
    feedbackText = ""
  }

  function handleRejectSubmit() {
    if (!rejectTaskId) return
    if (!feedbackText.trim()) {
      alert("Please provide feedback when rejecting a task")
      return
    }

    router.post(
      `/task_instances/${rejectTaskId}/reject`,
      {
        feedback: feedbackText,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          rejectTaskId = null
          feedbackText = ""
        },
      },
    )
  }

  function handleRejectCancel() {
    rejectTaskId = null
    feedbackText = ""
  }

  function handleTurnOverWeek() {
    turnOverWeekDialogOpen = true
  }

  function confirmTurnOverWeek() {
    router.post(
      "/dashboard/turn_over_week",
      {},
      {
        preserveScroll: true,
      },
    )
  }

  function handleCancelClick(taskId: number) {
    cancelTaskId = taskId
    cancelDialogOpen = true
  }

  function confirmCancel() {
    if (!cancelTaskId) return

    router.delete(`/task_instances/${cancelTaskId}`, {
      preserveScroll: true,
      onSuccess: () => {
        cancelTaskId = null
        cancelDialogOpen = false
      },
    })
  }

  function handleAssignClick(templateId: number) {
    const template = adHocAvailable.find((t) => t.id === templateId)
    if (!template) return

    if (template.available_to_id) {
      router.post(
        assignTaskTemplatePath(templateId),
        {},
        {
          preserveScroll: true,
        },
      )
    } else {
      assignTaskTemplateId = templateId
    }
  }

  function handleAssignSubmit(userId: number) {
    if (!assignTaskTemplateId) return

    router.post(
      "/task_instances/assign_ad_hoc",
      {
        task_template_id: assignTaskTemplateId,
        user_id: userId,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          assignTaskTemplateId = null
        },
      },
    )
  }

  function handleAssignCancel() {
    if (assignDialogRef) {
      assignDialogRef.close()
    }
    assignTaskTemplateId = null
  }

  $effect(() => {
    if (assignDialogRef && assignTaskTemplateId) {
      assignDialogRef.showModal()
      setTimeout(() => {
        const select = document.getElementById(
          "assign_user",
        ) as HTMLSelectElement
        if (select) {
          select.focus()
        }
      }, 0)
    }
  })
</script>

<svelte:head>
  <title>Dashboard - Chorizo</title>
</svelte:head>

<AppLayout {breadcrumbs}>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold">Dashboard</h1>
        <p class="text-muted-foreground mt-2">
          Overview of all tasks and activity
        </p>
      </div>
      {#if canTurnOverWeek}
        <Button onclick={handleTurnOverWeek} variant="default">
          Turn Over Week
        </Button>
      {/if}
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <!-- Awaiting Approval -->
      <Card>
        <CardHeader>
          <CardTitle>Awaiting Approval</CardTitle>
        </CardHeader>
        <CardContent>
          {#if awaitingApproval.length > 0}
            <div class="space-y-1">
              {#each awaitingApproval as task (task.id)}
                <div
                  class="hover:bg-muted/50 flex flex-col gap-2 rounded px-2 py-1.5 xl:flex-row xl:items-center xl:justify-between"
                >
                  <div
                    class="flex flex-1 items-center justify-between xl:justify-start xl:gap-3"
                  >
                    <div>
                      <div class="font-medium">
                        {task.task_template?.name || "Task"}
                      </div>
                      {#if task.assigned_to && task.completed_at}
                        <div class="text-muted-foreground text-sm">
                          {formatCompletedBy(task)}
                        </div>
                      {/if}
                    </div>
                    {#if task.task_template?.wage}
                      <span class="text-sm font-medium xl:hidden"
                        >${Number.parseFloat(task.task_template.wage).toFixed(
                          2,
                        )}</span
                      >
                    {/if}
                  </div>
                  <div class="flex items-center gap-3">
                    {#if task.task_template?.wage}
                      <span class="hidden text-sm font-medium xl:inline"
                        >${Number.parseFloat(task.task_template.wage).toFixed(
                          2,
                        )}</span
                      >
                    {/if}
                    <Button
                      size="sm"
                      onclick={() => handleApprove(task.id)}
                      class="h-7 px-2 text-xs"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      onclick={() => handleRejectClick(task.id)}
                      variant="destructive"
                      class="h-7 px-2 text-xs"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <p class="text-muted-foreground text-sm">
              No tasks awaiting approval
            </p>
          {/if}
        </CardContent>
      </Card>

      <!-- Currently Assigned -->
      <Card>
        <CardHeader>
          <CardTitle>Currently Assigned</CardTitle>
        </CardHeader>
        <CardContent>
          {#if currentlyAssigned.length > 0}
            {@const groupedByMember = currentlyAssigned.reduce(
              (acc, task) => {
                const memberId = task.assigned_to?.id ?? 0
                const memberName = task.assigned_to?.name ?? "Unassigned"
                if (!acc[memberId]) {
                  acc[memberId] = { name: memberName, tasks: [] }
                }
                acc[memberId].tasks.push(task)
                return acc
              },
              {} as Record<number, { name: string; tasks: TaskInstance[] }>,
            )}
            <div class="space-y-4">
              {#each Object.entries(groupedByMember) as [memberId, group] (memberId)}
                <div class="space-y-1">
                  <div class="text-muted-foreground text-sm font-medium">
                    {group.name}
                  </div>
                  {#each group.tasks as task (task.id)}
                    <div
                      class="hover:bg-muted/50 flex flex-col gap-2 rounded px-2 py-1.5 xl:flex-row xl:items-center xl:justify-between"
                    >
                      <div
                        class="flex flex-1 items-center justify-between xl:justify-start xl:gap-3"
                      >
                        <div class="font-medium">
                          {task.task_template?.name || "Task"}
                        </div>
                        {#if task.task_template?.wage}
                          <span class="text-sm font-medium xl:hidden"
                            >${Number.parseFloat(
                              task.task_template.wage,
                            ).toFixed(2)}</span
                          >
                        {/if}
                      </div>
                      <div class="flex items-center gap-3">
                        {#if task.task_template?.wage}
                          <span class="hidden text-sm font-medium xl:inline"
                            >${Number.parseFloat(
                              task.task_template.wage,
                            ).toFixed(2)}</span
                          >
                        {/if}
                        <Button
                          size="sm"
                          onclick={() => handleComplete(task.id)}
                          class="h-7 px-2 text-xs"
                        >
                          Complete
                        </Button>
                        <Button
                          size="sm"
                          onclick={() => handleCancelClick(task.id)}
                          variant="destructive"
                          class="h-7 px-2 text-xs"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  {/each}
                </div>
              {/each}
            </div>
          {:else}
            <p class="text-muted-foreground text-sm">
              No tasks currently assigned
            </p>
          {/if}
        </CardContent>
      </Card>

      <!-- Available Tasks -->
      <Card>
        <CardHeader>
          <CardTitle>Available Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {#if adHocAvailable.length > 0}
            <div class="space-y-1">
              {#each adHocAvailable as template (template.id)}
                <div
                  class="hover:bg-muted/50 flex flex-col gap-2 rounded px-2 py-1.5 xl:flex-row xl:items-center xl:justify-between"
                >
                  <div class="flex-1">
                    <div class="font-medium">{template.name}</div>
                    {#if template.available_to}
                      <div class="text-muted-foreground text-sm">
                        Available to {template.available_to.name}
                      </div>
                    {:else}
                      <div class="text-muted-foreground text-sm">
                        Available to any member
                      </div>
                    {/if}
                    {#if formatTaskLimit(template.min_instances, template.max_instances)}
                      <div class="text-muted-foreground text-sm">
                        {formatTaskLimit(
                          template.min_instances,
                          template.max_instances,
                        )}
                      </div>
                    {/if}
                  </div>
                  <Button
                    size="sm"
                    onclick={() => handleAssignClick(template.id)}
                    class="h-7 px-2 text-xs"
                  >
                    Assign
                  </Button>
                </div>
              {/each}
            </div>
          {:else}
            <p class="text-muted-foreground text-sm">
              No ad-hoc tasks available
            </p>
          {/if}
        </CardContent>
      </Card>

      <!-- Completed -->
      <Card>
        <CardHeader>
          <CardTitle>Completed</CardTitle>
        </CardHeader>
        <CardContent>
          {#if unpaidCompleted.length > 0}
            <div class="space-y-1">
              {#each unpaidCompleted as task (task.id)}
                <div
                  class="hover:bg-muted/50 flex items-center justify-between rounded px-2 py-1.5"
                >
                  <div class="flex-1">
                    <div class="font-medium">
                      {task.task_template?.name || "Task"}
                    </div>
                    {#if task.assigned_to && task.completed_at}
                      <div class="text-muted-foreground text-sm">
                        {formatCompletedBy(task)}
                      </div>
                    {/if}
                  </div>
                  {#if task.task_template?.wage}
                    {#if task.wage_earned === true}
                      <span class="text-sm font-medium"
                        >${Number.parseFloat(task.task_template.wage).toFixed(
                          2,
                        )}</span
                      >
                    {:else}
                      <span class="text-muted-foreground text-sm font-medium"
                        >--</span
                      >
                    {/if}
                  {/if}
                </div>
              {/each}
            </div>
          {:else}
            <p class="text-muted-foreground text-sm">
              No unpaid completed tasks
            </p>
          {/if}
        </CardContent>
      </Card>
    </div>

    <!-- Financial Summary -->
    <div class="md:max-w-[calc(50%-0.5rem)]">
      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <span class="font-semibold">Total Unpaid</span>
              <span class="text-lg font-bold">
                ${financialSummary.totalUnpaid.toFixed(2)}
              </span>
            </div>
            {#if financialSummary.byMember.length > 0}
              <div class="space-y-2 border-t pt-4">
                {#each financialSummary.byMember as summary (summary.user.id)}
                  {#if summary.totalUnpaid > 0}
                    <div class="space-y-1">
                      <button
                        type="button"
                        onclick={() => {
                          if (expandedMembers.includes(summary.user.id)) {
                            expandedMembers = expandedMembers.filter(
                              (id) => id !== summary.user.id,
                            )
                          } else {
                            expandedMembers = [
                              ...expandedMembers,
                              summary.user.id,
                            ]
                          }
                        }}
                        class="hover:bg-muted/50 flex w-full items-center justify-between rounded px-2 py-1.5"
                      >
                        <span>{summary.user.name}</span>
                        <div class="flex items-center gap-2">
                          <span class="font-medium">
                            ${summary.totalUnpaid.toFixed(2)}
                          </span>
                          <span class="text-muted-foreground text-xs">
                            {expandedMembers.includes(summary.user.id)
                              ? "−"
                              : "+"}
                          </span>
                        </div>
                      </button>
                      {#if expandedMembers.includes(summary.user.id)}
                        <div class="ml-4 space-y-1 border-l pl-2">
                          {#each summary.tasks as task (task.task_template.id)}
                            <div
                              class="flex items-center justify-between rounded px-2 py-1 text-sm"
                            >
                              <span class="text-muted-foreground">
                                {task.task_template?.name || "Task"}
                                {#if task.count > 1}
                                  ({task.count})
                                {/if}
                              </span>
                              <span class="font-medium">
                                ${Number.parseFloat(
                                  task.task_template?.wage?.toString() || "0",
                                ).toFixed(2)}
                              </span>
                            </div>
                          {/each}
                        </div>
                      {/if}
                    </div>
                  {/if}
                {/each}
              </div>
            {/if}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>

  <!-- Assign Dialog -->
  {#if assignTaskTemplateId}
    <dialog
      bind:this={assignDialogRef}
      onclose={() => {
        assignTaskTemplateId = null
      }}
      onkeydown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault()
          handleAssignCancel()
        }
      }}
      class="bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-md"
    >
      <form
        method="dialog"
        onsubmit={(e) => {
          e.preventDefault()
          const select = document.getElementById(
            "assign_user",
          ) as HTMLSelectElement
          const userId = Number.parseInt(select.value)
          if (userId) {
            handleAssignSubmit(userId)
          }
        }}
      >
        <h2 class="mb-4 text-lg font-semibold">Assign Task</h2>
        <p class="text-muted-foreground mb-4 text-sm">
          Select a member to assign this task to
        </p>
        <div class="space-y-4">
          <div>
            <label for="assign_user" class="mb-2 block text-sm font-medium"
              >Member</label
            >
            <select
              id="assign_user"
              name="assign_user"
              required
              class="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
            >
              <option value="">Select a member</option>
              {#each members as member (member.id)}
                <option value={member.id}>{member.name}</option>
              {/each}
            </select>
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              onclick={handleAssignCancel}
              class="border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md border px-4 py-2 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-medium"
            >
              Assign
            </button>
          </div>
        </div>
      </form>
    </dialog>
  {/if}

  <style>
    dialog::backdrop {
      background-color: rgb(0 0 0 / 0.5);
      backdrop-filter: blur(4px);
    }
  </style>

  <!-- Reject Dialog -->
  {#if rejectTaskId}
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-background w-full max-w-md rounded-lg border p-6 shadow-lg"
      >
        <h2 class="mb-4 text-lg font-semibold">Reject Task</h2>
        <p class="text-muted-foreground mb-4 text-sm">
          Please provide feedback for the member about why this task was
          rejected.
        </p>
        <div class="space-y-4">
          <div>
            <label for="reject_feedback" class="mb-2 block text-sm font-medium"
              >Feedback</label
            >
            <textarea
              id="reject_feedback"
              bind:value={feedbackText}
              rows="4"
              class="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Enter feedback for the member..."
              required
            ></textarea>
          </div>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              onclick={handleRejectCancel}
              class="border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md border px-4 py-2 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="button"
              onclick={handleRejectSubmit}
              class="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md px-4 py-2 text-sm font-medium"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
  <ConfirmationDialog
    bind:open={turnOverWeekDialogOpen}
    title="Turn Over Week"
    description="Are you sure you want to turn over the week? This will mark earned tasks as paid and expire incomplete tasks."
    onConfirm={confirmTurnOverWeek}
  />

  <ConfirmationDialog
    bind:open={cancelDialogOpen}
    title="Cancel Task"
    description="Are you sure you want to cancel this task? This action cannot be undone."
    confirmLabel="Cancel Task"
    variant="destructive"
    onConfirm={confirmCancel}
  />
</AppLayout>
